import { ReactNode } from 'react';

// mui
import {
  Avatar,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

export interface ActionAttachment {
  id: string;
  name: string;
  /** Optional secondary line, e.g. "100kb · Loading". */
  meta?: string;
  /** Upload in progress — shows a spinner + remove instead of the kebab. */
  isUploading?: boolean;
}

export interface ActionAttachmentsProps {
  attachments: ActionAttachment[];
  /** Fired when a ready row's kebab is pressed (download / view / …). */
  onOpenAttachment?: (id: string) => void;
  /** Fired when an uploading row's remove is pressed. */
  onRemoveAttachment?: (id: string) => void;
  /** Optional CTA shown above the list (e.g. an "Upload document" button). */
  uploadSlot?: ReactNode;
  /** Section header. */
  label?: string;
}

const AttachmentRow = ({
  attachment,
  onOpen,
  onRemove,
}: {
  attachment: ActionAttachment;
  onOpen?: (id: string) => void;
  onRemove?: (id: string) => void;
}) => (
  <Paper variant='outlined'>
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        gap: 3,
        p: 3,
      }}
    >
      <Avatar>
        <Icon icon='file-lines' fontSize={20} />
      </Avatar>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography variant='body1' noWrap>
          {attachment.name}
        </Typography>
        {attachment.meta && (
          <Typography
            variant='body2'
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {attachment.meta}
          </Typography>
        )}
      </Stack>
      {attachment.isUploading ? (
        <>
          <IconButton
            aria-label='Remove'
            onClick={() => onRemove?.(attachment.id)}
          >
            <Icon icon='trash' fontSize={20} />
          </IconButton>
          <CircularProgress size={20} />
        </>
      ) : (
        <IconButton
          aria-label='Document actions'
          edge='end'
          onClick={() => onOpen?.(attachment.id)}
        >
          <Icon icon='ellipsis-vertical' fontSize={20} />
        </IconButton>
      )}
    </Stack>
  </Paper>
);

/**
 * ActionAttachments renders the action's linked documents (incl. call notes) as
 * bordered rows, with an optional CTA above. Rows in flight show a progress
 * spinner + remove; ready rows show a kebab. Presentational only — upload and
 * row actions are delegated via callbacks.
 */
export const ActionAttachments = ({
  attachments,
  onOpenAttachment,
  onRemoveAttachment,
  uploadSlot,
  label = 'Uploads',
}: ActionAttachmentsProps) => (
  <Stack>
    <Typography variant='overline'>{label}</Typography>
    <Stack
      sx={{
        gap: 2,
      }}
    >
      {uploadSlot}
      {attachments.map((attachment) => (
        <AttachmentRow
          key={attachment.id}
          attachment={attachment}
          onOpen={onOpenAttachment}
          onRemove={onRemoveAttachment}
        />
      ))}
    </Stack>
  </Stack>
);

ActionAttachments.displayName = 'ActionAttachments';
export default ActionAttachments;
